import React from 'react';
import { Download } from 'lucide-react';
import { Button } from './button';
import { toast } from 'sonner';

/**
 * Props for the ExportActionBtn component
 */
interface ExportActionBtnProps {
  /** Function to fetch all data for export */
  fetchAllData: () => Promise<any[] | undefined>;
  /** Filename for the exported CSV (without .csv extension) */
  filename?: string;
  /** Button text */
  buttonText?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Button variant */
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  /** Additional class name */
  className?: string;
}

/**
 * A reusable Export button component for exporting data to CSV
 */
const ExportActionBtn: React.FC<ExportActionBtnProps> = ({
  fetchAllData,
  filename = 'export_data',
  buttonText = 'Export',
  disabled = false,
  variant = 'outline',
  className = '',
}) => {
  const handleExport = async () => {
    try {
      const data = await fetchAllData();
      
      if (data && data.length > 0) {
        // Get headers from the first item
        const headers = Object.keys(data[0]);
        
        // Convert each item to CSV row
        const csvRows = data.map(item => 
          headers.map(header => {
            const value = (item as any)[header];
            // Handle objects by converting to JSON string
            if (typeof value === 'object' && value !== null) {
              return JSON.stringify(value);
            }
            // Handle strings with commas by wrapping in quotes
            if (typeof value === 'string' && value.includes(',')) {
              return `"${value}"`;
            }
            return value;
          }).join(',')
        );
        
        // Create CSV content with headers and rows
        const csvContent = [headers.join(','), ...csvRows].join('\n');
        
        // Create and download the file
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        
        toast.success(`Successfully exported to ${filename}.csv`);
      } else {
        toast.error('No data available to export');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };

  return (
    <Button
      variant={variant}
      onClick={handleExport}
      disabled={disabled}
      className={className}
    >
      <Download className="h-4 w-4 mr-2" />
      {buttonText}
    </Button>
  );
};

export default ExportActionBtn;
